import { baseApi } from "../api";
import { GoalStatusFilter } from "../types";
import {
  CreateInvestmentGoalPayload,
  InvestmentGoalResponse,
  UpdateInvestmentGoalPayload,
} from "./types";

interface UpdateInvestmentGoalArgs {
  id: number;
  payload: UpdateInvestmentGoalPayload;
}

const investmentGoalsListTags = (result?: InvestmentGoalResponse[]) => [
  { type: "InvestmentGoal" as const, id: "LIST" },
  ...(result?.map((goal) => ({
    type: "InvestmentGoal" as const,
    id: goal.id,
  })) ?? []),
];

const invalidateInvestmentGoal = (id: number) => [
  { type: "InvestmentGoal" as const, id },
  { type: "InvestmentGoal" as const, id: "LIST" },
];

const invalidateDeletedInvestmentGoal = (id: number) => [
  ...invalidateInvestmentGoal(id),
  "HealthTargets" as const,
];

export const investmentGoalsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvestmentGoals: builder.query<
      InvestmentGoalResponse[],
      GoalStatusFilter | void
    >({
      query: (status) => ({
        url: "/investment-goals",
        params: status ? { status } : undefined,
      }),
      providesTags: investmentGoalsListTags,
    }),

    getInvestmentGoal: builder.query<InvestmentGoalResponse, number>({
      query: (id) => ({ url: "/investment-goals/" + id }),
      providesTags: (_result, _error, id) => [{ type: "InvestmentGoal", id }],
    }),

    createInvestmentGoal: builder.mutation<
      unknown,
      CreateInvestmentGoalPayload
    >({
      query: (payload) => ({
        url: "/investment-goals",
        method: "post",
        data: payload,
      }),
      invalidatesTags: [{ type: "InvestmentGoal", id: "LIST" }],
    }),

    updateInvestmentGoal: builder.mutation<void, UpdateInvestmentGoalArgs>({
      query: ({ id, payload }) => ({
        url: "/investment-goals/" + id,
        method: "patch",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateInvestmentGoal(id),
    }),

    archiveInvestmentGoal: builder.mutation<InvestmentGoalResponse, number>({
      query: (id) => ({
        url: "/investment-goals/" + id + "/archive",
        method: "post",
      }),
      invalidatesTags: (_result, _error, id) => invalidateInvestmentGoal(id),
    }),

    deleteInvestmentGoal: builder.mutation<void, number>({
      query: (id) => ({
        url: "/investment-goals/" + id,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, id) =>
        invalidateDeletedInvestmentGoal(id),
    }),
  }),
});

export const {
  useGetInvestmentGoalsQuery,
  useGetInvestmentGoalQuery,
  useCreateInvestmentGoalMutation,
  useUpdateInvestmentGoalMutation,
  useArchiveInvestmentGoalMutation,
  useDeleteInvestmentGoalMutation,
} = investmentGoalsApi;
